import './page.less'

import {m, wrap} from '@codeurs/front'
import {ArrowIcon, ShareIcon} from 'assets/inline-icons'
import format from 'date-fns/format'
import React, {Component, Fragment, FunctionComponent} from 'react'
import {
  BlocksData,
  CardData,
  CategoryItemType,
  EntryData,
  PageData,
  SubCategoryType
} from 'types'
import {t, useLanguage} from 'util/i18n'
import {PageLink} from 'util/pagelink'
import {getLocale} from 'util/params'
import {scrollTo} from 'util/scrollmemory'
import {style} from 'util/style'
import {isIE} from 'util/tools'
import {Blocks} from 'views/blocks/blocks'
import {HarmonicaBlock} from 'views/blocks/harmonicablock'
import {CardContainer, CardImage, CardTitle} from 'views/layout/card'
import {ArticleCard} from 'views/partials/articlegrid'
import {HeaderPortal} from 'views/partials/header'
import {Hero} from 'views/partials/hero'
import {Newsletter} from 'views/partials/newsletter'
import {Share} from 'views/partials/share'
import {Sidebar} from 'views/partials/sidebar'
import {Theme} from 'views/theme'
import {Layout} from '../layout'

const styles = style('page', ['subpage', 'hero', 'related', 'totop', 'share'])

type HasHarmonicaSubpages = {
  show_harmonica: true
  subpages?: Array<PageData & BlocksData & {page_title?: string}>
}

type PageSubCategory =
  | EntryData
  | {
      id: number
    }
  | undefined

@wrap(Layout)
export class Page extends Component<{
  data: PageData &
    BlocksData & {
      text: string
      parents?: Array<EntryData>
      page_title?: string
      related: {
        articles: Array<CardData>
        publication: Array<CardData>
      }
      publication_date?: string
    } & HasHarmonicaSubpages
}> {
  state = {
    smallSidebar: false
  }

  componentDidMount(): void {
    this.checkSidebarHeight()
  }

  componentDidUpdate(prevProps, prevState, snapshot?: any): void {
    this.checkSidebarHeight()
  }

  checkSidebarHeight = () => {
    const {id} = this.props.data
    const sidebar = document.getElementById('sidebar' + id)
    if (!sidebar) return
    if (
      sidebar.clientHeight &&
      sidebar.clientHeight < 300 &&
      !this.state.smallSidebar
    ) {
      this.setState({smallSidebar: true})
      return
    }
    if (
      sidebar.clientHeight &&
      sidebar.clientHeight >= 300 &&
      this.state.smallSidebar
    ) {
      this.setState({smallSidebar: false})
    }
  }

  render() {
    const {
      language,
      page_title,
      title,
      text,
      blocks,
      id,
      hero,
      url,
      show_grid,
      show_harmonica,
      subsite,
      subpages = [],
      newsletter
    } = this.props.data
    const sidenav = this.getSidenav()
    const getGrid: Array<CategoryItemType> = this.getChildrenGrid()
    return (
      <Fragment>
        <div class={styles}>
          <div class={styles.share('wrapper')}>
            <Theme.Container>
              <div class={styles.share('wrapper-helper')}>
                <PageShare copy={page_title} url={url} />
              </div>
            </Theme.Container>
          </div>
          {sidenav && (
            <div class={styles('sidebar-container')}>
              <Theme.Container class={styles('container')}>
                <div class={styles('sidebar-container-bar').mod({IE: isIE()})}>
                  <div
                    class={styles('sidebar').mod({
                      small: this.state.smallSidebar
                    })}
                  >
                    <Sidebar
                      pageId={id}
                      category={this.getPageCategory()}
                      {...(sidenav as SubCategoryType)}
                    />
                  </div>
                </div>
              </Theme.Container>
            </div>
          )}
          <div class={styles('overflow')}>
            <Theme.Container class={styles('container')}>
              <HeaderPortal>
                <Hero small={!!sidenav} hero={!!(hero && hero.image)}>
                  <Theme.H1>{page_title || title}</Theme.H1>
                </Hero>
              </HeaderPortal>
              {sidenav && (
                <div
                  class={styles('sidebar-placeholder').mod({
                    small: this.state.smallSidebar
                  })}
                />
              )}
              <div class={styles('content').mod({sidenav})}>
                {show_grid && (
                  <SubPageGrid>
                    {getGrid.map(item => (
                      <SubPageCard {...item} />
                    ))}
                  </SubPageGrid>
                )}
                <this.PublicationDate />
                {blocks && blocks.length > 0 && (
                  <div class={styles('content-blocks')}>
                    <Blocks blocks={blocks} pageId={id} />
                  </div>
                )}
                <div class={styles.totop('container')}>
                  {blocks && blocks.length > 2 && (
                    <button
                      title="To top"
                      class={styles.totop}
                      onClick={() => {
                        scrollTo(() => 0, 0, 'smooth')
                      }}
                    >
                      <ArrowIcon className={styles.totop('icon')} />
                    </button>
                  )}
                </div>
                {show_harmonica &&
                  subpages.map(page => (
                    <HarmonicaBlock
                      title={page.page_title || page.title}
                      content={page.blocks || []}
                    ></HarmonicaBlock>
                  ))}
                <this.RelatedItems small={!!sidenav} />
              </div>
            </Theme.Container>
          </div>
        </div>
        <Newsletter {...newsletter} language={language} />
      </Fragment>
    )
  }
  getChildrenGrid = () => {
    const {show_grid, id, categories} = this.props.data
    if (!show_grid || !id || !categories) return null

    const pageSubcategory: PageSubCategory = this.getPageSubCategory()

    if (!pageSubcategory) return null
    const currentparent = categories.find(cat =>
      cat.children.find(subcat => subcat.id === pageSubcategory.id)
    )
    if (!currentparent) return null
    const currentpage = currentparent.children.find(page =>
      page.children.find(subpage => subpage.id === id)
    )
    if (!currentpage) return null
    const currentSubpage = currentpage.children.find(
      subsubpage => subsubpage.id === id
    )
    if (!currentSubpage) return null

    return currentSubpage.children
  }

  PublicationDate = () => {
    const {channel, publication_date} = this.props.data
    if (['news_item'].indexOf(channel) < 0) return null
    if (!publication_date) return null
    return (
      <div class={styles('date')}>
        {format(new Date(publication_date.replace(/-/g, '/')), 'd MMMM yyyy', {
          locale: getLocale(useLanguage())
        })}
      </div>
    )
  }

  getSidenav(): SubCategoryType {
    const {parents, categories} = this.props.data
    if (!parents || parents.length === 0) return null
    const pageSubcategory: PageSubCategory = this.getPageSubCategory()

    const pageCategory = this.getPageCategory()
    if (!pageCategory) return null
    const category = categories.find(cat => cat.id === pageCategory.id)
    const result: SubCategoryType = category.children.find(
      subcat => subcat.id === pageSubcategory?.id
    )
    if (!result?.children.length) return null
    const {id, show_harmonica} = this.props.data
    if (!show_harmonica) return result
    return {
      ...result,
      children: result.children.map(child => {
        if (child.channel !== 'parent_page') return child
        return {
          ...child,
          children: child.id === id ? [] : child.children
        }
      })
    } as any
  }

  getPageCategory() {
    const {parents} = this.props.data

    return parents.find(p => p.channel === 'category')
  }

  getPageSubCategory(): PageSubCategory {
    const {channel, id, parents} = this.props.data
    return channel === 'subcategory'
      ? {id}
      : parents.find(p => p.channel === 'subcategory')
  }

  RelatedItems = ({small}) => {
    const {related, tagslist, tagspath} = this.props.data
    if (!related.articles.length && !related.publication.length) return null
    return (
      <div class={styles.related.mod({small})}>
        <div class={styles.related('content')}>
          {related.articles.length > 0 &&
            related.articles.map((article, i) => (
              <div class={styles.related('item')}>
                {i === 0 && (
                  <Theme.P class={styles.related('title')}>
                    {t('contentpage.related_articles')}
                  </Theme.P>
                )}
                <ArticleCard
                  item={article}
                  tagspath={tagspath}
                  tagslist={tagslist}
                />
              </div>
            ))}
          {related.publication.length > 0 &&
            related.publication.map((publication, i) => (
              <div class={styles.related('item')}>
                {i === 0 && (
                  <Theme.P class={styles.related('title')}>
                    {t('contentpage.related_publication')}
                  </Theme.P>
                )}
                <ArticleCard
                  item={publication}
                  tagspath={tagspath}
                  tagslist={tagslist}
                />
              </div>
            ))}
        </div>
      </div>
    )
  }
}

const SubPageCard: React.FC<CategoryItemType> = ({...item}) => {
  const {url, image, title} = item
  if (!url) return null
  return (
    <div {...styles.subpage('card-gaps')}>
      <PageLink to={url}>
        <div className={styles.subpage('card')}>
          {image && <CardImage {...image} />}
          {title && (
            <CardContainer>
              <CardTitle>{title}</CardTitle>
            </CardContainer>
          )}
        </div>
      </PageLink>
    </div>
  )
}

const SubPageGrid: React.FC = ({children, ...props}) => {
  return (
    <div className={styles.subpage('grid')} {...props}>
      {children}
    </div>
  )
}

export const PageShare: FunctionComponent<{copy: string; url: string}> = ({
  url,
  copy
}) => {
  return (
    <div class={styles.share('container')}>
      <Share text={copy} url={window.location.protocol + url}>
        <div class={styles.share}>
          <ShareIcon />
          <div class={styles.share('text')}>{t('sharepage')}</div>
        </div>
      </Share>
    </div>
  )
}
